import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Mashy from "@/components/Mashy";

const GOALS = [
  { id: "spoken_english", label: "Improve spoken English", emoji: "🎙️" },
  { id: "math", label: "Improve math", emoji: "🧮" },
  { id: "confidence", label: "Build confidence", emoji: "💪" },
  { id: "exams", label: "Prepare for school exams", emoji: "📚" },
];
const LEVELS = ["beginner", "basic", "intermediate"];
const LANGS = [
  { id: "hindi", label: "हिंदी" },
  { id: "english", label: "English" },
  { id: "hinglish", label: "Hinglish" },
];
const DAILY = [5, 10, 15];

function Pill({ active, onClick, children, testId }) {
  return (
    <motion.button
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      data-testid={testId}
      className={`btn-chunky w-full text-base ${
        active
          ? "bg-[#1CB0F6] border-[#1899D6] text-white"
          : "bg-white border-gray-300 text-gray-700"
      }`}
    >
      {children}
    </motion.button>
  );
}

export default function Onboarding() {
  const nav = useNavigate();
  const { fetchMe } = useAuth();
  const [step, setStep] = useState(0);
  const [goals, setGoals] = useState([]);
  const [level, setLevel] = useState("beginner");
  const [language, setLanguage] = useState("hinglish");
  const [daily, setDaily] = useState(10);
  const [saving, setSaving] = useState(false);

  const toggleGoal = (g) =>
    setGoals((arr) => (arr.includes(g) ? arr.filter((x) => x !== g) : [...arr, g]));

  const next = () => setStep((s) => Math.min(3, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    setSaving(true);
    try {
      await api.put("/users/me", {
        goals,
        learner_level: level,
        preferred_language: language,
        daily_goal_minutes: daily,
        grade_level: null,
      });
      await fetchMe();
      nav("/dashboard");
    } finally {
      setSaving(false);
    }
  };

  const steps = [
    {
      title: "Aapka goal kya hai?",
      sub: "Choose one or more",
      body: (
        <div className="grid grid-cols-1 gap-3">
          {GOALS.map((g) => (
            <Pill
              key={g.id}
              active={goals.includes(g.id)}
              onClick={() => toggleGoal(g.id)}
              testId={`onboard-goal-${g.id}`}
            >
              <span className="text-xl mr-2">{g.emoji}</span>
              {g.label}
            </Pill>
          ))}
        </div>
      ),
    },
    {
      title: "Aapka level?",
      sub: "Honest rehna — Mashy support karega",
      body: (
        <div className="grid grid-cols-1 gap-3">
          {LEVELS.map((l) => (
            <Pill key={l} active={level === l} onClick={() => setLevel(l)} testId={`onboard-level-${l}`}>
              {l[0].toUpperCase() + l.slice(1)}
            </Pill>
          ))}
        </div>
      ),
    },
    {
      title: "Preferred language?",
      sub: "Mashy will explain in this language",
      body: (
        <div className="grid grid-cols-1 gap-3">
          {LANGS.map((l) => (
            <Pill key={l.id} active={language === l.id} onClick={() => setLanguage(l.id)} testId={`onboard-lang-${l.id}`}>
              {l.label}
            </Pill>
          ))}
        </div>
      ),
    },
    {
      title: "Daily goal?",
      sub: "Small daily wins = big progress",
      body: (
        <div className="grid grid-cols-3 gap-3">
          {DAILY.map((d) => (
            <Pill key={d} active={daily === d} onClick={() => setDaily(d)} testId={`onboard-daily-${d}`}>
              {d} mins
            </Pill>
          ))}
        </div>
      ),
    },
  ];

  const cur = steps[step];

  return (
    <div className="min-h-screen bg-[#F7F9FC] flex flex-col items-center px-5 py-8" data-testid="onboarding-screen">
      <div className="w-full max-w-md flex items-center gap-4 mb-6">
        <Mashy mood="happy" size={70} />
        <div className="flex-1">
          <div className="text-xs text-gray-500 font-bold uppercase tracking-widest">Step {step + 1} / 4</div>
          <div className="w-full bg-gray-200 rounded-full h-3 mt-1 overflow-hidden">
            <div className="h-full bg-[#58CC02]" style={{ width: `${((step + 1) / 4) * 100}%` }} />
          </div>
        </div>
      </div>
      <motion.div
        key={step}
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="w-full max-w-md card-chunky p-6 flex flex-col gap-5"
      >
        <div>
          <h2 className="font-display text-2xl">{cur.title}</h2>
          <p className="text-gray-500 font-semibold">{cur.sub}</p>
        </div>
        {cur.body}
      </motion.div>

      <div className="w-full max-w-md flex gap-3 mt-6">
        {step > 0 && (
          <button onClick={prev} className="btn-ghost flex-1" data-testid="onboard-prev">
            Back
          </button>
        )}
        {step < 3 ? (
          <button
            onClick={next}
            disabled={step === 0 && goals.length === 0}
            data-testid="onboard-next"
            className="btn-primary flex-1 disabled:opacity-50 disabled:active:translate-y-0 disabled:active:border-b-4"
          >
            Next
          </button>
        ) : (
          <button onClick={submit} disabled={saving} data-testid="onboard-finish" className="btn-success flex-1">
            {saving ? "Saving..." : "Start Learning!"}
          </button>
        )}
      </div>
    </div>
  );
}
